import React, { useRef, useState } from "react";
import { Button } from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CheckIcon from "@material-ui/icons/Check";
import {
  EventNote,
  EmojiObjects,
  Assignment,
  VolumeUp,
  FontDownload,
  Assessment,
  MenuBook,
  CalendarToday,
  EmojiEmotions,
} from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import "./FeaturedTools.css";

const TOOLS = [
  {
    id: "planner",
    image: "01-activity-planner",
    title: "Activity Planner",
    group: "Classroom",
    icon: EventNote,
    description:
      "Weekly planning across language, math, practical life, and more—so every child works holistically at their own pace.",
  },
  {
    id: "activities",
    image: "02-activities",
    title: "Activities",
    group: "Classroom",
    icon: EmojiObjects,
    description:
      "Hands-on Montessori tasks chosen by age and development. The app records what each child does and how well they did it.",
  },
  {
    id: "lessons",
    image: "03-lesson-plans",
    title: "Lesson Plans",
    group: "Classroom",
    icon: MenuBook,
    description:
      "Pre-designed plans with objectives, materials, and procedures so teachers prepare and deliver with confidence.",
  },
  {
    id: "sounds",
    image: "04-charts-sounds",
    title: "Charts – Sounds",
    group: "Language",
    icon: VolumeUp,
    description:
      "Track phonetic letter sounds for early reading and writing, and plan what each student practices next.",
  },
  {
    id: "alphabets",
    image: "05-movable-alphabets",
    title: "Charts – Movable Alphabets",
    group: "Language",
    icon: FontDownload,
    description:
      "Track words each child has learned with the Movable Alphabet and plan the next steps in language development.",
  },
  {
    id: "assignments",
    image: "06-assignments",
    title: "Assignments",
    group: "Families",
    icon: Assignment,
    description:
      "Activities tailored to each child's level and interests. Parents see what's assigned, completed, and open for resubmission.",
  },
  {
    id: "calendar",
    image: "07-calendar-of-events",
    title: "Calendar of Events",
    group: "School life",
    icon: CalendarToday,
    description:
      "A clear view of key school activities and dates, helping teachers, students, and families plan throughout the year.",
  },
  {
    id: "reports",
    image: "08-reports",
    title: "Reports",
    group: "Insights",
    icon: Assessment,
    description:
      "Plan and track weekly progress across all areas of work—at each child's pace while meeting learning goals.",
  },
  {
    id: "stories",
    image: "09-kids-own-stories",
    title: "Kids Own Stories",
    group: "Families",
    icon: EmojiEmotions,
    description: null,
    bullets: [
      "AI-driven interest mapping & career matching",
      "Adaptive learning pathways & content suggestions",
      "Educator dashboards & progress analytics",
      "Parent engagement & real-time updates",
      "Cloud-based & scalable for institutions",
    ],
  },
];

const SHOT = (file) => `${process.env.PUBLIC_URL || ""}/images/montestory-workspace-images/${file}.png`;

const pad = (n) => String(n).padStart(2, "0");

const FeaturedTools = () => {
  const history = useHistory();
  const [active, setActive] = useState(0);
  const tabRefs = useRef([]);

  const select = (index, focus = false) => {
    const next = (index + TOOLS.length) % TOOLS.length;
    setActive(next);
    if (focus && tabRefs.current[next]) tabRefs.current[next].focus();
  };

  const onKeyDown = (event) => {
    if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      event.preventDefault();
      select(active + 1, true);
    } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      event.preventDefault();
      select(active - 1, true);
    } else if (event.key === "Home") {
      event.preventDefault();
      select(0, true);
    } else if (event.key === "End") {
      event.preventDefault();
      select(TOOLS.length - 1, true);
    }
  };

  const tool = TOOLS[active];

  return (
    <section className="featured-tools">
      <div className="featured-tools__inner">
        <header className="featured-tools__header">
          <p className="featured-tools__eyebrow">Platform capabilities</p>
          <h2 className="featured-tools__title">One workspace for the whole classroom</h2>
          <p className="featured-tools__lead">
            Explore the tools Montessori teachers, school leaders, and families use every day.
          </p>
        </header>

        <div className="tools-explorer">
          <div
            className="tools-explorer__list"
            role="tablist"
            aria-label="Featured tools"
            aria-orientation="vertical"
            onKeyDown={onKeyDown}
          >
            {TOOLS.map(({ id, title, icon: Icon }, index) => {
              const selected = index === active;
              return (
                <button
                  key={id}
                  ref={(el) => {
                    tabRefs.current[index] = el;
                  }}
                  id={`tool-tab-${id}`}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  aria-controls="tool-panel"
                  tabIndex={selected ? 0 : -1}
                  className={`tools-explorer__tab${selected ? " is-active" : ""}`}
                  onClick={() => select(index)}
                >
                  <span className="tools-explorer__num">{pad(index + 1)}</span>
                  <Icon className="tools-explorer__tab-icon" />
                  <span className="tools-explorer__tab-title">{title}</span>
                  <ArrowForwardIcon className="tools-explorer__tab-arrow" />
                </button>
              );
            })}
          </div>

          <div
            className="tools-explorer__panel"
            id="tool-panel"
            role="tabpanel"
            aria-labelledby={`tool-tab-${tool.id}`}
            key={tool.id}
          >
            <div className="tools-explorer__panel-top">
              <span className="tools-explorer__count">
                {pad(active + 1)} / {pad(TOOLS.length)}
              </span>
            </div>

            <h3 className="tools-explorer__panel-title">{tool.title}</h3>

            {tool.bullets ? (
              <ul className="tools-explorer__bullets">
                {tool.bullets.map((item) => (
                  <li key={item}>
                    <CheckIcon className="tools-explorer__check" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="tools-explorer__text">{tool.description}</p>
            )}

            {tool.image ? (
              <figure className="tools-explorer__shot">
                <img src={SHOT(tool.image)} alt={`${tool.title} screen preview`} loading="lazy" />
              </figure>
            ) : null}

            <div className="tools-explorer__panel-foot">
              <div className="tools-explorer__nav">
                <button
                  type="button"
                  className="tools-explorer__navbtn"
                  aria-label="Previous tool"
                  onClick={() => select(active - 1)}
                >
                  <ArrowBackIcon fontSize="small" />
                </button>
                <button
                  type="button"
                  className="tools-explorer__navbtn"
                  aria-label="Next tool"
                  onClick={() => select(active + 1)}
                >
                  <ArrowForwardIcon fontSize="small" />
                </button>
              </div>
              <span
                className="tools-explorer__progress"
                aria-hidden
                style={{ "--progress": `${((active + 1) / TOOLS.length) * 100}%` }}
              />
            </div>
          </div>
        </div>

        <aside className="featured-tools__cta">
          <div>
            <h3 className="featured-tools__cta-title">Ready to transform your school?</h3>
            <p className="featured-tools__cta-text">
              Join Montessori schools improving learning outcomes with Montestory.
            </p>
          </div>
          <Button
            variant="contained"
            className="featured-tools__cta-btn"
            endIcon={<ArrowForwardIcon />}
            onClick={() => history.push("/montessori/schoolregistration")}
          >
            Start free trial
          </Button>
        </aside>
      </div>
    </section>
  );
};

export default FeaturedTools;
